<template>
	<main>
		<!-- Hero Section -->

		<div class="hero_section">
			<div class="menu">
				<a
					v-for="(genre, index) in visibleGenres"
					:key="index"
					href="#"
					class="sub-menu"
					@click.prevent="scrollToGenre(genre)"
				>
					<img :src="getGenreIcon(genre)" alt="" /> {{ $t(genre) }}
				</a>

				<template v-if="showOthers">
					<a
						v-for="(genre, index) in hiddenGenres"
						:key="'hidden-' + index"
						:href="'/all-genres#' + genre.replace(/\s+/g, '-')"
						class="sub-menu"
						@click.prevent="navigateToGenre(genre)"
					>
						<img :src="getGenreIcon(genre)" alt="" /> {{ $t(genre) }}
					</a>
				</template>

				<a href="#" class="sub-menu" v-if="hiddenGenres.length > 0" @click.prevent="toggleGenres">
					{{ showOthers ? $t('Show Less') : $t('Others') }}
				</a>
			</div>

			<div class="hero_content">
				<div class="hero_left">
					<h1>{{ $t('Unlock Your Next Adventure') }}</h1>
					<p>
						{{
							$t(
								'Explore a world of endless fun and excitement. Dive into the best games, exclusive offers, and the latest updates. Start playing today'
							)
						}}
					</p>

					<div class="button_section">
						<a href="/all-genres" class="browse">{{ $t('Browse Games') }}</a>
						<img class="console" src="@/assets/console.png" alt="" />
					</div>
				</div>

				<div class="hero_right">
					<img src="@/assets/hero_img.png" alt="" />
				</div>
			</div>
		</div>

		<!-- Game Content Section -->

		<div class="content_section">
			<div v-if="loading" class="loader-overlay">
				<div class="loader">
					<div class="loader-spinner">
						<div
							v-for="(n, index) in 12"
							:key="index"
							:style="{
								animationDelay: `${-((12 - index) / 12)}s`,
								transform: `rotate(${(index + 1) * 30}deg)`,
							}"
						></div>
					</div>
				</div>
			</div>

			<div v-if="!loading">
				<div
					v-for="(gamesList, genre) in categorizedGames"
					:key="genre"
					:id="genre.replace(/\s+/g, '-')"
				>
					<!-- Genre Title -->
					<div class="content_title">
						<span>{{ $t(genre) }}</span>
					</div>

					<!-- Game Grid -->
					<section class="content_grid">
						<router-link
							v-for="game in gamesList.slice(0, 8)"
							:key="game._id"
							:to="{ name: 'GameDetails', params: { id: game._id } }"
						>
							<div class="game_card">
								<img
									:alt="game.name"
									class="card_img"
									decoding="async"
									height="250"
									loading="lazy"
									:src="game.banner_sm_url"
									width="250"
								/>
								<div class="card_title">{{ $t(game.name) }}</div>
							</div>
						</router-link>
					</section>
				</div>
			</div>
		</div>

		<div class="others">
			<router-link to="/all-genres" class="others-button"> {{ $t('All Games') }} </router-link>
		</div>
	</main>
</template>

<script>
import gamesData from '@/assets/games.json';

import trendingIcon from '@/assets/Menu-icon/trending.png';
import likeIcon from '@/assets/Menu-icon/like.png';
import heartIcon from '@/assets/Menu-icon/heart.png';
import newIcon from '@/assets/Menu-icon/new.png';

export default {
	data() {
		return {
			games: gamesData,
			genres: ['Latest Games', 'Exclusive Games', 'Popular Games', 'Kids'],
			showOthers: false,
			allGenres: [],
			loading: true,
		};
	},

	computed: {
		categorizedGames() {
			const categorized = {};
			this.genres.forEach((genre) => {
				categorized[genre] = this.games.filter((game) => game.genres.includes(genre));
			});
			return categorized;
		},

		visibleGenres() {
			return this.genres;
		},

		hiddenGenres() {
			return this.allGenres.filter((genre) => !this.genres.includes(genre));
		},

		filteredCategories() {
			return Object.keys(this.categorizedGames);
		},
	},

	created() {
		this.fetchGames();
	},

	methods: {
		async fetchGames() {
			this.loading = true; // Show loader before fetching
			try {
				const response = await fetch('/games.json'); // Fetch the game data
				if (!response.ok) throw new Error('Failed to load games.json');

				const data = await response.json();
				this.games = data; // Make sure this updates correctly
				this.loadGenres();
			} catch (error) {
				console.error('Error loading games:', error);
			} finally {
				this.loading = false; // Hide loader after fetching is done
			}
		},

		scrollToGenre(genre) {
			const genreId = genre.replace(/\s+/g, '-'); // Replace spaces with "-"
			const element = document.getElementById(genreId);
			if (element) {
				element.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		},

		navigateToGenre(genre) {
			this.$router.push({ path: '/all-genres', query: { genre: genre.replace(/\s+/g, '-') } });

			this.$nextTick(() => {
				const element = document.getElementById(genre.replace(/\s+/g, '-'));
				if (element) {
					element.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			});
		},

		loadGenres() {
			const genreSet = new Set();
			this.games.forEach((game) => {
				game.genres.forEach((genre) => genreSet.add(genre));
			});
			this.allGenres = Array.from(genreSet);
		},

		toggleGenres() {
			this.showOthers = !this.showOthers;
		},

		getGenreIcon(genre) {
			const icons = {
				'Latest Games': trendingIcon,
				'Exclusive Games': likeIcon,
				'Popular Games': heartIcon,
				'Kids Games': newIcon,
			};
			return icons[genre] || newIcon; // Ensuring a default icon
		},
	},
};
</script>

<style>
.menu {
	overflow-y: auto;
}
</style>
